import { FC, useState, useEffect, useRef, useMemo } from 'react';
import classNames from 'classnames';
import { ApolloQueryResult } from '@apollo/client';
import { UseFieldArrayUpdate } from 'react-hook-form';
import { PlusSmIcon } from '@heroicons/react/outline';

import {
  Exact,
  PatientQuery,
  SignatureUser,
  MeProviderQuery,
  PatientDataFragment,
  ProgramAssessmentSignatureUserInput,
} from '../../../../../generated/graphql';

import {
  FollowUpForm,
  SavedActivityInputPreview,
} from '../../../../lib/followUp';
import { getColorForSignatureUserType } from '../../../../lib/multiSignature';
import { isProviderUserOwnerOrAdminAuthorized } from '../../../../../lib/auth';

import Alert from '../../../Alert';
import Button from '../../../Button';
import SelectMenu from '../../../SelectMenu';
import ModalDialog from '../../../ModalDialog';
import ContentPreview from '../../../ContentPreview';
import { MODAL_TRANSITION_DURATION } from '../../../Modal';
import AssignProviderUsersModal from '../../ClientAssignmentModals/AssignProviderUsersModal';
import { customToast } from '../../../ToastAlert/customToast';

import {
  checkNotEnoughAssignedProviderUsers,
  initializeSelectedAssignedProviderUsers,
  getCommonAssignedProviderUsers,
  findAvailableProviderUser,
} from './helpers';

interface CustomSelectOptionProps {
  value: { value: string; label: string };
}

const CustomSelectOption: FC<CustomSelectOptionProps> = ({ value }) => {
  return (
    <div
      className={classNames(
        'flex w-full flex-row items-center justify-start rounded-md',
      )}
    >
      {value.label}
    </div>
  );
};

type SelectedProgramAssessmentSignatureUser =
  ProgramAssessmentSignatureUserInput & {
    name: string;
  };

export type SavedActivityInputPreviewWithActivityIndex =
  SavedActivityInputPreview & {
    activityIndex: number;
  };

type ReviewSignerDetailsModalProps = {
  setClosed: () => void;
  isOpen: boolean;
  sendingProviderUser: MeProviderQuery['meProvider'] | null;
  multiSignatureActivities: SavedActivityInputPreviewWithActivityIndex[];
  patient?: PatientDataFragment;
  patients?: PatientDataFragment[];
  refetchPatient?: (
    variables?: Partial<Exact<{ programId: string }>>,
  ) => Promise<ApolloQueryResult<PatientQuery>>;
  refetchPatientForModal?: (programId: string) => Promise<void>;
  update: UseFieldArrayUpdate<FollowUpForm, 'activities'>;
  sendFollowUpWithCheck: (skipConfirmation?: boolean) => Promise<void>;
  submitting: boolean;
  /** Override final confirm CTA (default: Confirm & Sign / Confirm & Send). */
  confirmCompleteLabel?: string;
};

const ReviewSignerDetailsModal: FC<ReviewSignerDetailsModalProps> = ({
  isOpen,
  setClosed,
  multiSignatureActivities,
  sendingProviderUser,
  patient,
  patients,
  refetchPatient,
  refetchPatientForModal,
  update,
  sendFollowUpWithCheck,
  submitting,
  confirmCompleteLabel,
}) => {
  // Support both single patient (backward compatibility) and multiple patients
  const patientsList = patients || (patient ? [patient] : []);
  const isMultipleRecipients = patientsList.length > 1;
  // Get single patient for single recipient case (used for assign button and warnings)
  const singlePatient = isMultipleRecipients
    ? undefined
    : patientsList[0] || patient;
  const [currentActivityIndex, setCurrentActivityIndex] = useState(0);
  const [isAssignProviderUsersModalOpen, setIsAssignProviderUsersModalOpen] =
    useState(false);

  const currentActivity = multiSignatureActivities?.[currentActivityIndex];
  const assessmentSignatureUsers =
    currentActivity?.multiSignatureAssessmentConfig?.assessmentSignatureUsers;

  const [selectedAssignedProviderUsers, setSelectedAssignedProviderUsers] =
    useState<{ [key: string]: string }>({});

  // Track the last selected team member to persist across activities
  const lastSelectedTeamMemberId = useRef<string | null>(null);

  const hasInitialized = useRef(false);
  const justInitialized = useRef(false);

  const canAssignPractitioners =
    isProviderUserOwnerOrAdminAuthorized(sendingProviderUser);

  // Memoize common assigned provider users to avoid recalculating multiple times
  // This will automatically update when patientsList changes (e.g., after refetch)
  const commonAssignedProviderUsers = useMemo(
    () => getCommonAssignedProviderUsers(patientsList, sendingProviderUser),
    [patientsList, sendingProviderUser],
  );

  // Memoize assigned provider user types to avoid filtering multiple times
  const assignedProviderUserTypes = useMemo(
    () =>
      assessmentSignatureUsers?.filter(
        (user) => user.signatureUserType === SignatureUser.AssignedProviderUser,
      ) || [],
    [assessmentSignatureUsers],
  );

  // Memoize available provider user IDs set for quick lookups
  const availableProviderUserIds = useMemo(
    () => new Set(commonAssignedProviderUsers.map((user) => user.id)),
    [commonAssignedProviderUsers],
  );

  // Helper to reset modal state
  const resetModalState = () => {
    setCurrentActivityIndex(0);
    lastSelectedTeamMemberId.current = null;
    setSelectedAssignedProviderUsers({});
  };

  const resetAndSetClosed = () => {
    setTimeout(() => {
      resetModalState();
    }, MODAL_TRANSITION_DURATION);
    setClosed();
  };

  useEffect(() => {
    if (!isOpen || hasInitialized.current) {
      return;
    }

    // Wait until recipient patient data (with assigned team members) is available
    if (patientsList.length === 0) {
      return;
    }

    // Clear previous activity's selections before initializing new activity
    // This ensures we start fresh for each activity but use last selected team member
    setSelectedAssignedProviderUsers({});

    initializeSelectedAssignedProviderUsers(
      assessmentSignatureUsers,
      sendingProviderUser,
      patient,
      patients,
      setSelectedAssignedProviderUsers,
      lastSelectedTeamMemberId.current,
      availableProviderUserIds,
    );

    hasInitialized.current = true;
    justInitialized.current = true;
  }, [
    isOpen,
    currentActivityIndex,
    availableProviderUserIds,
    patientsList.length,
  ]);

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasInitialized.current = false;
    }
  }, [isOpen]);

  // Re-initialize selections when patient care-team data changes (e.g. after assigning)
  const assignedProviderUsersFingerprint = useMemo(
    () =>
      commonAssignedProviderUsers
        .map((user) => user.id)
        .sort()
        .join(','),
    [commonAssignedProviderUsers],
  );

  useEffect(() => {
    if (
      isOpen &&
      hasInitialized.current &&
      assessmentSignatureUsers &&
      patientsList.length > 0 &&
      commonAssignedProviderUsers.length > 0
    ) {
      // Re-initialize to pick up newly assigned provider users
      // Only update missing selections, don't override existing user selections
      initializeSelectedAssignedProviderUsers(
        assessmentSignatureUsers,
        sendingProviderUser,
        singlePatient,
        patients,
        setSelectedAssignedProviderUsers,
        lastSelectedTeamMemberId.current,
        availableProviderUserIds,
      );
    }
  }, [assignedProviderUsersFingerprint, isOpen]);

  // Fallback initialization to ensure all AssignedProviderUser fields have values
  // Skip this effect if modal is closing to prevent selection reset during transition
  // Also skip if we just initialized to prevent overriding the initialization
  useEffect(() => {
    // Skip this run if we just initialized (check synchronously, then reset flag)
    if (justInitialized.current) {
      justInitialized.current = false;
      return; // Skip this run
    }

    if (
      assessmentSignatureUsers &&
      patientsList.length > 0 &&
      hasInitialized.current &&
      isOpen // Only run if modal is still open
    ) {
      // Check if there are any AssignedProviderUser types missing from selectedAssignedProviderUsers
      const missingSelections = assignedProviderUserTypes.filter(
        (user) => !selectedAssignedProviderUsers[user.id],
      );

      // Clear invalid selections (where selected provider is not in available options)
      const invalidSelections = Object.keys(
        selectedAssignedProviderUsers,
      ).filter((assessmentSignatureUserId) => {
        const selectedProviderUserId =
          selectedAssignedProviderUsers[assessmentSignatureUserId];
        const isAssignedProviderUserType = assignedProviderUserTypes.some(
          (user) => user.id === assessmentSignatureUserId,
        );
        return (
          isAssignedProviderUserType &&
          selectedProviderUserId &&
          !availableProviderUserIds.has(selectedProviderUserId)
        );
      });

      // Only update if there are missing selections or invalid selections, and we have available providers
      if (
        (missingSelections.length > 0 || invalidSelections.length > 0) &&
        commonAssignedProviderUsers.length > 0
      ) {
        const updatedSelected: { [key: string]: string } = {
          ...selectedAssignedProviderUsers,
        };
        let hasChanges = false;

        // Clear invalid selections
        for (const invalidId of invalidSelections) {
          delete updatedSelected[invalidId];
          hasChanges = true;
        }

        // Fill missing selections
        for (const assessmentSignatureUser of missingSelections) {
          const excludeIds = Object.values(updatedSelected);
          const availableProviderUser = findAvailableProviderUser(
            commonAssignedProviderUsers,
            availableProviderUserIds, // Can now pass Set directly
            lastSelectedTeamMemberId.current,
            excludeIds,
          );

          if (availableProviderUser) {
            updatedSelected[assessmentSignatureUser.id] =
              availableProviderUser.id;
            hasChanges = true;
          }
        }

        if (hasChanges) {
          setSelectedAssignedProviderUsers(updatedSelected);
        }
      } else if (invalidSelections.length > 0) {
        // Clear invalid selections even if we don't have available providers
        const updatedSelected: { [key: string]: string } = {
          ...selectedAssignedProviderUsers,
        };
        for (const invalidId of invalidSelections) {
          delete updatedSelected[invalidId];
        }
        setSelectedAssignedProviderUsers(updatedSelected);
      }
    }
  }, [
    assessmentSignatureUsers,
    patientsList,
    selectedAssignedProviderUsers,
    sendingProviderUser,
    assignedProviderUserTypes,
    commonAssignedProviderUsers,
    availableProviderUserIds,
  ]);

  const handleAssignedProviderUserChange = (
    assessmentSignatureUserId: string,
    providerUserId: string,
  ) => {
    if (!providerUserId) {
      return;
    }

    // Store the last selected team member for persistence across activities
    lastSelectedTeamMemberId.current = providerUserId;

    setSelectedAssignedProviderUsers((prev) => {
      const newSelected = { ...prev };

      const existingKey = Object.keys(newSelected).find(
        (key) => newSelected[key] === providerUserId,
      );

      if (existingKey) {
        newSelected[existingKey] = newSelected[assessmentSignatureUserId];
      }

      newSelected[assessmentSignatureUserId] = providerUserId;
      return newSelected;
    });
  };

  // Helper to build program assessment signature user for display
  const buildProgramAssessmentSignatureUser = (
    assessmentSignatureUser: typeof assessmentSignatureUsers[0],
  ): SelectedProgramAssessmentSignatureUser => {
    const baseUser: SelectedProgramAssessmentSignatureUser = {
      assessmentSignatureUserId: assessmentSignatureUser.id,
      name: '',
    };

    switch (assessmentSignatureUser.signatureUserType) {
      case SignatureUser.PatientUser:
        return {
          ...baseUser,
          name: isMultipleRecipients
            ? `${patientsList.length} recipients`
            : patientsList[0]?.name,
        };
      case SignatureUser.SendingProviderUser:
        return {
          ...baseUser,
          providerUserId: sendingProviderUser?.id,
          name: sendingProviderUser?.name,
        };
      case SignatureUser.AssignedProviderUser:
        const assignedProviderUserId =
          selectedAssignedProviderUsers[assessmentSignatureUser.id];
        const assignedProviderUser = commonAssignedProviderUsers.find(
          (user) => user.id === assignedProviderUserId,
        );

        return {
          ...baseUser,
          providerUserId: assignedProviderUser?.id,
          name: assignedProviderUser?.name,
        };
      default:
        return baseUser;
    }
  };

  const programAssessmentSignatureUsers = assessmentSignatureUsers?.map(
    buildProgramAssessmentSignatureUser,
  );

  const isNotLastActivity =
    currentActivityIndex < multiSignatureActivities?.length - 1;

  const hasSendingProviderFirstSigner = multiSignatureActivities?.some(
    (activity) =>
      activity.multiSignatureAssessmentConfig?.assessmentSignatureUsers?.[0]
        ?.signatureUserType === SignatureUser.SendingProviderUser,
  );

  const confirmButtonText = isNotLastActivity
    ? 'Confirm signers'
    : confirmCompleteLabel ??
      `Confirm & ${hasSendingProviderFirstSigner ? 'Sign' : 'Send'}`;

  // Helper function to check if all AssignedProviderUser types have selections
  const areAllAssignedProviderUsersSelected = (): boolean => {
    if (assignedProviderUserTypes.length === 0) return true;

    // Check that all AssignedProviderUser types have valid selections
    return assignedProviderUserTypes.every((user) => {
      const selectedProviderUserId = selectedAssignedProviderUsers[user.id];
      return (
        selectedProviderUserId !== undefined &&
        availableProviderUserIds.has(selectedProviderUserId)
      );
    });
  };

  const handleConfirm = async () => {
    // Prevent sending if recipients don't share common assigned provider users
    if (hasNoCommonAssignedProviders) {
      customToast.error(
        'The selected recipients do not share any common assigned team members. Please ensure all recipients have at least one team member in common, or send to recipients individually.',
      );
      return;
    }

    // Validate all AssignedProviderUser types have selections
    const missingSelections = assignedProviderUserTypes.filter(
      (user) => !selectedAssignedProviderUsers[user.id],
    );

    if (missingSelections && missingSelections.length > 0) {
      customToast.error(
        'Please select a team member for all required signers.',
      );
      return;
    }

    // Validate that all selected provider users are actually available
    const invalidSelections = assignedProviderUserTypes.filter((user) => {
      const selectedProviderUserId = selectedAssignedProviderUsers[user.id];
      return (
        selectedProviderUserId &&
        !availableProviderUserIds.has(selectedProviderUserId)
      );
    });

    if (invalidSelections.length > 0) {
      customToast.error(
        'One or more selected team members are not available for all recipients. Please select different team members.',
      );
      return;
    }

    // Build program assessment signature users for saving
    const programAssessmentSignatureUsers: ProgramAssessmentSignatureUserInput[] =
      assessmentSignatureUsers?.map(
        ({ id: assessmentSignatureUserId, signatureUserType }) => {
          const baseInput = {
            assessmentSignatureUserId,
          };

          const isSendingProviderUser =
            signatureUserType === SignatureUser.SendingProviderUser;
          const isAssignedProviderUser =
            signatureUserType === SignatureUser.AssignedProviderUser;

          if (isSendingProviderUser) {
            return {
              ...baseInput,
              providerUserId: sendingProviderUser?.id,
            };
          }
          if (isAssignedProviderUser) {
            return {
              ...baseInput,
              providerUserId:
                selectedAssignedProviderUsers[assessmentSignatureUserId],
            };
          }
          // PatientUser - no providerUserId needed
          return baseInput;
        },
      );

    const updatedMultiSignatureActivity = {
      ...currentActivity,
      sendFollowUpActivityInputRaw: {
        ...currentActivity.sendFollowUpActivityInputRaw,
        programAssessmentSignatureUsers,
      },
    };

    update(currentActivity.activityIndex, updatedMultiSignatureActivity);

    if (isNotLastActivity) {
      // Don't clear selections - keep them in state so they persist visually
      // Reset initialization flag so next activity initializes with last selected team member
      hasInitialized.current = false;
      setCurrentActivityIndex(currentActivityIndex + 1);
    } else {
      // Last activity - close modal immediately (before transition) to prevent selection reset
      // This provides immediate visual feedback while submission happens in background
      setClosed();
      // Clear state after modal closes to prevent any re-renders from resetting selection
      setTimeout(() => {
        resetModalState();
      }, MODAL_TRANSITION_DURATION);
      // Await submission to catch errors (errors will show via toast even if modal is closed)
      await sendFollowUpWithCheck(true);
    }
  };

  const activityPreviewCardProps = currentActivity?.activityPreviewCardProps;

  const notEnoughAssignedProviders = checkNotEnoughAssignedProviderUsers(
    assessmentSignatureUsers,
    patient,
    patients,
    sendingProviderUser,
  );

  const hasNoCommonAssignedProviders =
    isMultipleRecipients &&
    assignedProviderUserTypes.length > 0 &&
    commonAssignedProviderUsers.length === 0;

  return (
    <>
      {canAssignPractitioners && !isMultipleRecipients && (
        <AssignProviderUsersModal
          isOpen={isAssignProviderUsersModalOpen}
          selectedPatient={singlePatient}
          refetchPatient={refetchPatient}
          setClosed={async () => {
            // Refetch patient data for modal to get updated assignedProviderUsers
            if (singlePatient?.programInstanceId && refetchPatientForModal) {
              await refetchPatientForModal(singlePatient.programInstanceId);
            }
            // Selections re-initialize via the effect on commonAssignedProviderUsers
            setIsAssignProviderUsersModalOpen(false);
          }}
        />
      )}
      <ModalDialog
        title="Review form signers"
        width="medium"
        isBlocking={isAssignProviderUsersModalOpen}
        overflow="auto"
        isOpen={isOpen}
        setClosed={resetAndSetClosed}
        bodyClassName="flex flex-col justify-start"
        footerClassName="border-none"
        rightFooter={
          <div className="flex w-full flex-row items-center justify-end">
            <Button
              className="mr-3"
              title="Cancel"
              size="small"
              theme="secondary"
              onClick={resetAndSetClosed}
            />
            {notEnoughAssignedProviders &&
              canAssignPractitioners &&
              !isMultipleRecipients &&
              singlePatient && (
                <Button
                  className="mr-3"
                  title={`Assign team members to ${singlePatient.firstName}`}
                  size="small"
                  iconPosition="left"
                  onClick={() => setIsAssignProviderUsersModalOpen(true)}
                  IconComponent={PlusSmIcon}
                />
              )}
            {!notEnoughAssignedProviders && !hasNoCommonAssignedProviders && (
              <Button
                onClick={handleConfirm}
                title={!submitting ? confirmButtonText : 'Sending...'}
                disabled={
                  submitting ||
                  hasNoCommonAssignedProviders ||
                  !areAllAssignedProviderUsersSelected()
                }
                size="small"
              />
            )}
          </div>
        }
      >
        <div className="mb-5 text-caption">
          Review the signers and signing order. You can edit the form in your
          Library.
          {isMultipleRecipients && (
            <div className="mt-2 text-caption font-medium text-green-150">
              This form will be sent to {patientsList.length} recipients. Team
              member selections apply to all recipients.
            </div>
          )}
        </div>
        {activityPreviewCardProps && (
          <ContentPreview
            className="mb-4 rounded-md border border-neutral-75 px-4 py-3 shadow-none"
            contentType={activityPreviewCardProps.contentType}
            title={activityPreviewCardProps.title}
            description={activityPreviewCardProps.description}
            previewThumbnailImageUrl={
              activityPreviewCardProps.previewThumbnailImageUrl
            }
            disableClick
            toggleActivityOrResourceLabel={false}
          />
        )}

        <div className="flex w-full flex-col items-center justify-start">
          {assessmentSignatureUsers?.map((assessmentSignatureUser, index) => {
            const {
              orderIndex,
              signatureUserType,
              id: assessmentSignatureUserId,
            } = assessmentSignatureUser;

            const { backgroundColor, borderColor } =
              getColorForSignatureUserType(signatureUserType);
            const isAssignedProviderUser =
              signatureUserType === SignatureUser.AssignedProviderUser;

            const selectedProviderUser = commonAssignedProviderUsers.find(
              (assignedProviderUser) =>
                assignedProviderUser.id ===
                selectedAssignedProviderUsers[assessmentSignatureUserId],
            );

            const hasSendingProviderUser = assessmentSignatureUsers?.some(
              (assessmentSignatureUser) =>
                assessmentSignatureUser.signatureUserType ===
                SignatureUser.SendingProviderUser,
            );

            const fieldOptions = commonAssignedProviderUsers
              .map((assignedProviderUser) => ({
                value: assignedProviderUser.id,
                label: assignedProviderUser.name,
              }))
              .filter((fieldOption) => {
                // Only filter out the sending provider user if this is an assigned provider user field
                // and the sending provider is already being used as a sending provider user
                if (
                  isAssignedProviderUser &&
                  hasSendingProviderUser &&
                  fieldOption.value === sendingProviderUser?.id
                ) {
                  return false;
                }

                return true;
              });

            const fieldValue = isAssignedProviderUser
              ? selectedProviderUser
                ? {
                    value: selectedProviderUser?.id,
                    label: selectedProviderUser?.name,
                  }
                : fieldOptions?.[0] // Fallback to first available option if none selected
                ? {
                    value: fieldOptions[0].value,
                    label: fieldOptions[0].label,
                  }
                : ''
              : {
                  value:
                    programAssessmentSignatureUsers[index]
                      ?.assessmentSignatureUserId,
                  label: programAssessmentSignatureUsers[index]?.name,
                };

            return (
              <div
                key={assessmentSignatureUserId}
                className="mb-3 flex w-full items-center justify-between rounded-xl border border-neutral-50"
              >
                <div
                  className={classNames(
                    'flex w-full flex-grow items-center rounded-l-xl border-l-[14px] p-4',
                    borderColor,
                  )}
                >
                  <div className="flex h-10 w-full items-center text-body text-neutral-125">
                    <div
                      className={classNames(
                        'mr-4 flex h-full w-10 flex-shrink-0 items-center justify-center rounded-md',
                        backgroundColor,
                      )}
                    >
                      {orderIndex + 1}
                    </div>
                    <SelectMenu
                      label=""
                      hideLabel
                      disabled={
                        !isAssignedProviderUser || fieldOptions.length === 0
                      }
                      fieldValue={fieldValue}
                      onChange={(
                        value: { value: string; label: string } | string,
                      ) => {
                        // The SelectMenu passes the raw value, not the object
                        const providerUserId =
                          typeof value === 'object' ? value.value : value;
                        handleAssignedProviderUserChange(
                          assessmentSignatureUserId,
                          providerUserId,
                        );
                      }}
                      fieldOptions={fieldOptions}
                      placeholder="Choose a team member"
                      SelectOptionComponent={CustomSelectOption}
                      buttonClassName={classNames(
                        'flex h-10 w-full items-center justify-start rounded-md',
                        backgroundColor,
                      )}
                      containerClassName={classNames(
                        'flex h-10 w-full items-center justify-start rounded-md',
                        backgroundColor,
                      )}
                    />
                  </div>
                </div>
              </div>
            );
          })}
          {hasNoCommonAssignedProviders && (
            <Alert
              level="error"
              className="mt-2"
              message="The selected recipients do not share any common assigned team members. Please ensure all recipients have at least one team member in common, or send to recipients individually."
            />
          )}
          {notEnoughAssignedProviders && !hasNoCommonAssignedProviders && (
            <Alert
              level="warning"
              className="mt-2"
              message={`To send this form, you need to assign a team member to ${
                isMultipleRecipients
                  ? 'all recipients'
                  : singlePatient?.firstName || 'the recipient'
              } since their signature is required.`}
            />
          )}
        </div>
      </ModalDialog>
    </>
  );
};

export default ReviewSignerDetailsModal;
